/**
 * Whether the signed-in user is still using a password somebody else chose for them.
 *
 * <p>The backend answers this once, at sign-in, in the login response. Until 2026-08-10 every
 * operator account was created with the operator's own email address as its password and nothing
 * ever asked for a change — so on the first sign-in after that fix, everyone is in this state.
 *
 * <p>Kept in localStorage next to the tokens, and cleared the moment the password is changed.
 * localStorage rather than a module variable so it survives a page reload mid-flow, and rather
 * than localStorage so it dies with the tab along with the session it describes.
 *
 * <p>This is a guided flow, not an access control. It routes the console and nags on every sign-in;
 * it is not what stops anyone doing anything, and it is not trying to be. The real controls are the
 * role checks on the backend.
 */

const KEY = "npe.must-change-password";
const ACK_KEY = "npe.must-change-password.ack";


export const setMustChangePassword = (required) => {
    try {
        if (required) {
            localStorage.setItem(KEY, "1");
            // A fresh requirement deserves a fresh explanation — this runs at sign-in, so the
            // dialog is shown once per session rather than once per browser tab lifetime.
            localStorage.removeItem(ACK_KEY);
        } else {
            localStorage.removeItem(KEY);
            localStorage.removeItem(ACK_KEY);
        }
    } catch (e) {
        // Storage denied (private mode, locked-down browser). The flow degrades to "not required",
        // which is the right way to fail: a console nobody can use is worse than a delayed rotation.
    }
};

export const isPasswordChangeRequired = () => {
    try {
        return localStorage.getItem(KEY) === "1";
    } catch (e) {
        return false;
    }
};

/**
 * Whether the explanatory dialog has already been read this session.
 *
 * <p>Kept next to the flag rather than in component state so that it means "this person has read it",
 * not "this component instance has not re-rendered since". Component state resets on any remount and
 * on reload, either of which would put the dialog back in front of somebody who is already typing
 * their new password.
 */
export const acknowledgePasswordPrompt = () => {
    try {
        localStorage.setItem(ACK_KEY, "1");
    } catch (e) {
        // Storage denied — the dialog will reappear, which is annoying but not harmful.
    }
};

export const isPasswordPromptAcknowledged = () => {
    try {
        return localStorage.getItem(ACK_KEY) === "1";
    } catch (e) {
        return false;
    }
};

/** Where the user is sent, and where the password form lives. One definition, three callers. */
export const PASSWORD_SCREEN = "/dashboard/settings/profile";

export default isPasswordChangeRequired;
